import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { safeFetch } from "@/lib/api";
import { connectDashboardWS } from "@/lib/websocket";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const AppCtx = createContext(null);

const EMPTY_FILTERS = {
  venture: "", category: "", subcategory: "", severity: "",
  state: "", platform: "", q: "", start: "", end: "", window: "30d",
};

const ROLE_PRESETS = {
  executive: { severity: "critical", window: "24h" },
  analyst: {},
  pr: { platform: "twitter", window: "7d" },
};

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedDetail, setSelectedDetail] = useState(null); // { type, payload }
  const [selectedPost, setSelectedPost] = useState(null);

  const [kpis, setKpis] = useState(null);
  const [posts, setPosts] = useState([]);
  const [livePosts, setLivePosts] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [locations, setLocations] = useState({ states: [], sites: [] });
  const [severityDist, setSeverityDist] = useState([]);
  const [platformDist, setPlatformDist] = useState([]);
  const [categoryDist, setCategoryDist] = useState([]);
  const [ventureDist, setVentureDist] = useState([]);
  const [ventureBreakdown, setVentureBreakdown] = useState([]);
  const [topSites, setTopSites] = useState([]);
  const [sentimentTrend, setSentimentTrend] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [tickerSignals, setTickerSignals] = useState([]);
  const [wsStatus, setWsStatus] = useState({ connected: false, mock: false });
  const [source, setSource] = useState("loading");

  const paramsFromFilters = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  const loadAll = useCallback(async () => {
    const [
      k, p, l, loc, hier, sev, plat, cat, vent, trend,
    ] = await Promise.all([
      safeFetch("/kpis", { params: paramsFromFilters }),
      safeFetch("/posts", { params: { ...paramsFromFilters, limit: 200 } }),
      safeFetch("/posts/live", { params: { limit: 60 } }),
      safeFetch("/locations", { params: paramsFromFilters }),
      safeFetch("/hierarchy", { params: paramsFromFilters }),
      safeFetch("/reports/severity", { params: paramsFromFilters }),
      safeFetch("/platform-distribution", { params: paramsFromFilters }),
      safeFetch("/activity-distribution", { params: paramsFromFilters }),
      safeFetch("/reports/area", { params: paramsFromFilters }),
      safeFetch("/sentiment-trend", { params: { bucket: "day", ...paramsFromFilters } }),
    ]);
    setKpis(k.data);
    setPosts(Array.isArray(p.data?.items) ? p.data.items : Array.isArray(p.data) ? p.data : []);
    setLivePosts(Array.isArray(l.data) ? l.data : []);
    setLocations(loc.data || { states: [], sites: [] });
    setHierarchy(Array.isArray(hier.data) ? hier.data : []);
    setSeverityDist(Array.isArray(sev.data) ? sev.data : []);
    setPlatformDist(Array.isArray(plat.data) ? plat.data : []);
    setCategoryDist(Array.isArray(cat.data) ? cat.data : []);
    setVentureDist(Array.isArray(vent.data) ? vent.data : []);
    setVentureBreakdown(Array.isArray(vent.data) ? vent.data : []);
    setTopSites(Array.isArray(loc.data?.markers) ? loc.data.markers : []);
    setSentimentTrend(Array.isArray(trend.data) ? trend.data : []);
    setSource(k.source === "live" ? "live" : "offline");
    const sample = (Array.isArray(p.data?.items) ? p.data.items : Array.isArray(p.data) ? p.data : []).slice(0, 20);
    setTickerSignals(sample);
  }, [paramsFromFilters]);

  useEffect(() => { 
    if (user) loadAll(); 
  }, [loadAll, user]);

  const loadAllRef = React.useRef(loadAll);
  useEffect(() => { loadAllRef.current = loadAll; }, [loadAll]);

  // WebSocket connection
  useEffect(() => {
    if (!user) return;
    const ws = connectDashboardWS({
      onOpen: (info) => setWsStatus({ connected: true, mock: !!info.mock }),
      onClose: () => setWsStatus((s) => ({ ...s, connected: false })),
      onMessage: (msg) => {
        if (!msg || !msg.event) return;
        if (msg.event === "NEW_POSTS") {
          const arr = Array.isArray(msg.payload) ? msg.payload : [msg.payload];
          setLivePosts((prev) => [...arr, ...prev].slice(0, 80));
          setTickerSignals((prev) => [...arr, ...prev].slice(0, 24));
          // Refresh metrics in real-time
          loadAllRef.current();
        } else if (msg.event === "HIGH_RISK_ALERT") {
          const alert = { id: `a_${Date.now()}_${Math.random()}`, at: new Date().toISOString(), ...msg.payload };
          setAlerts((prev) => [alert, ...prev].slice(0, 50));
          setTickerSignals((prev) => [{ ...msg.payload, _alert: true }, ...prev].slice(0, 24));
          // Important: Trigger refresh for risk aggregates
          loadAllRef.current();
          toast.error(`HIGH RISK: ${alert.site || alert.venture || "signal"}`, {
            id: alert.id,
            description: alert.content?.slice(0, 90) || "Critical signal detected",
          });
        } else if (msg.event === "MULTI_HIGH_RISK_ALERT") {
          toast.warning(`Multi-signal spike`, { description: "Correlated high-risk activity across sources" });
        }
      },
    });
    return () => ws.close();
  }, [user]);

  const applyRolePreset = (role) => {
    setFilters({ ...EMPTY_FILTERS, ...(ROLE_PRESETS[role] || {}) });
  };

  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const patchFilters = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const value = {
    filters, setFilters, patchFilters, clearFilters, applyRolePreset,
    activeTab, setActiveTab,
    selectedDetail, setSelectedDetail,
    selectedPost, setSelectedPost,
    showUnifiedInsight: true,
    kpis, posts, livePosts, hierarchy, locations, severityDist,
    platformDist, categoryDist, ventureDist, ventureBreakdown, topSites, sentimentTrend,
    alerts, tickerSignals, wsStatus, source,
    refresh: loadAll,
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export const useApp = () => useContext(AppCtx);
